import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FiMenu, FiX, FiPhone, FiUser, FiLogOut, FiPercent, FiChevronDown, FiMail, FiStar, FiBriefcase, FiFileText } from 'react-icons/fi';
import { useSettings } from '../context/SettingsContext';
import JoinHandsModal from './JoinHandsModal';
import QuoteModal from './quote/QuoteModal';
import HeaderSplitCta from './HeaderSplitCta';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_ec907cab-2737-4c0e-a014-2b1aed568ccf/artifacts/x38mi3mr_LOGO-Picsart-AiImageEnhancer-Picsart-AiImageEnhancer-Picsart-AiImageEnhancer-Picsart-AiImageEnhancer-Picsart-AiImageEnhancer.jpg";

const NAV_LINKS = [
  { path: '/', label: 'Home' },
  { path: '/villas', label: 'Villas' },
  { path: '/shooting', label: 'Film Shoots' },
  { path: '/corporate', label: 'Corporate' },
  { path: '/blog', label: 'Blog' },
  {
    path: '/contact',
    label: 'Contact',
    children: [
      { path: '/contact', label: 'Contact Us', icon: FiMail, desc: 'Get in touch — enquiries, directions, callbacks' },
      { path: '/feedback', label: 'Guest Feedback', icon: FiStar, desc: 'Share your post-stay experience with us' },
    ],
  },
];

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showPromo, setShowPromo] = useState(true);
  const [user, setUser] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const isHomePage = location.pathname === '/';
  const { nightDiscountPct, nightDiscountEnabled } = useSettings();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    const stored = localStorage.getItem('user');
    setUser(stored ? JSON.parse(stored) : null);
  }, [location]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    navigate('/');
  };

  const showWhiteBg = isScrolled || !isHomePage;
  const [showJoinHands, setShowJoinHands] = useState(false);
  const [showQuote, setShowQuote] = useState(false);

  return (
    <>
      {/* Sticky Promo Strip */}
      {showPromo && nightDiscountEnabled && (
        <div data-testid="promo-strip" className="fixed top-0 left-0 right-0 z-[60] bg-gradient-to-r from-[#F57C20] via-[#FF9A4D] to-[#F57C20] py-2 px-4">
          <div className="max-w-7xl mx-auto flex items-center justify-center gap-3 relative">
            <FiPercent className="w-4 h-4 text-white flex-shrink-0" />
            <p className="text-white text-xs sm:text-sm font-semibold text-center">
              Stay Longer, Pay Less: {nightDiscountPct}% OFF 2nd Night Onwards!
              <Link to="/booking" className="underline ml-2 hover:no-underline">Book Now</Link>
            </p>
            <button
              onClick={() => setShowPromo(false)}
              data-testid="promo-strip-close"
              className="absolute right-0 text-white/80 hover:text-white p-1"
              aria-label="Close promo"
            >
              <FiX className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Main Header */}
      <header
        data-testid="main-header"
        className={`fixed left-0 right-0 z-50 transition-all duration-500 ${
          showWhiteBg
            ? 'bg-white shadow-md'
            : 'bg-transparent'
        }`}
        style={{ top: showPromo ? '36px' : '0' }}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" data-testid="header-logo" className="flex items-center gap-3 group flex-shrink-0 min-w-0">
              <img
                src={LOGO_URL}
                alt="Royal Garden Villas"
                className="h-10 w-10 md:h-12 md:w-12 object-contain flex-shrink-0"
              />
              <span className="flex flex-col leading-[1.1] whitespace-nowrap" style={{ fontFamily: "'Playfair Display', serif" }}>
                <span className="text-lg sm:text-xl md:text-xl xl:text-2xl tracking-tight" style={{ fontWeight: 600 }}>
                  <span className="text-[#F57C20]">Royal</span>
                  <span className={showWhiteBg ? ' text-[#1A1A2E]' : ' text-white'}> Garden Villas</span>
                </span>
                <span
                  data-testid="header-brand-tagline"
                  className={`text-[9px] md:text-[11px] tracking-[0.35em] uppercase mt-[3px] self-end pr-0.5 ${showWhiteBg ? 'text-[#6B7280]' : 'text-white/70'}`}
                  style={{ fontFamily: "'Inter', 'Helvetica Neue', sans-serif", fontWeight: 500, letterSpacing: '0.35em', lineHeight: 1 }}
                >
                  Lonavala
                </span>
              </span>
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden lg:flex items-center gap-3 xl:gap-5 flex-shrink min-w-0" data-testid="desktop-nav">
              {NAV_LINKS.map(link => (
                link.children ? (
                  <div key={link.path} className="relative group">
                    <button
                      data-testid={`nav-${link.label.toLowerCase()}-toggle`}
                      className={`flex items-center gap-1 whitespace-nowrap text-sm font-medium tracking-wide transition-colors duration-300 ${
                        location.pathname.startsWith(link.path) || link.children.some(c => c.path === location.pathname)
                          ? 'text-[#F57C20]'
                          : showWhiteBg
                            ? 'text-[#1A1A2E]/70 hover:text-[#F57C20]'
                            : 'text-white/80 hover:text-white'
                      }`}
                    >
                      {link.label}
                      <FiChevronDown className="w-3.5 h-3.5 transition-transform duration-200 group-hover:rotate-180" />
                    </button>
                    {/* Dropdown */}
                    <div
                      data-testid={`nav-${link.label.toLowerCase()}-dropdown`}
                      className="absolute right-0 top-full pt-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50"
                    >
                      <div className="bg-white border border-gray-100 rounded-xl shadow-xl shadow-black/10 py-2 w-72">
                        {link.children.map(child => {
                          const Icon = child.icon;
                          return (
                            <Link
                              key={child.path}
                              to={child.path}
                              data-testid={`nav-${child.label.toLowerCase().replace(/\s+/g, '-')}`}
                              className="flex items-start gap-3 px-4 py-3 hover:bg-[#F57C20]/5 transition-colors group/item"
                            >
                              <div className="w-8 h-8 rounded-lg bg-[#F57C20]/10 flex items-center justify-center flex-shrink-0 group-hover/item:bg-[#F57C20]/20 transition-colors">
                                <Icon className="w-4 h-4 text-[#F57C20]" />
                              </div>
                              <div className="min-w-0">
                                <p className="text-[#1A1A2E] text-sm font-semibold leading-tight">{child.label}</p>
                                <p className="text-[#6B7280] text-[11px] leading-snug mt-0.5">{child.desc}</p>
                              </div>
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                ) : (
                  <Link
                    key={link.path}
                    to={link.path}
                    data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
                    className={`text-sm font-medium whitespace-nowrap tracking-wide transition-colors duration-300 ${
                      location.pathname === link.path
                        ? 'text-[#F57C20]'
                        : showWhiteBg
                          ? 'text-[#1A1A2E]/70 hover:text-[#F57C20]'
                          : 'text-white/80 hover:text-white'
                    }`}
                  >
                    {link.label}
                  </Link>
                )
              ))}
              {/* Join Hands With Us — opens modal (B2B agent partnership) */}
              <button
                type="button"
                data-testid="nav-join-hands"
                onClick={() => setShowJoinHands(true)}
                className="relative inline-flex items-center gap-1.5 whitespace-nowrap flex-shrink-0 text-sm font-semibold tracking-wide px-3.5 py-1.5 rounded-full border transition-all duration-300 bg-gradient-to-r from-[#F57C20] to-[#D4621A] text-white border-[#F57C20]/0 hover:shadow-lg hover:shadow-[#F57C20]/40 hover:scale-[1.03]"
              >
                <FiBriefcase className="w-3.5 h-3.5" />
                Join Hands
              </button>
            </nav>

            {/* Right side */}
            <div className="flex items-center gap-3 flex-shrink-0 ml-4">
              <a
                href="tel:+919820088292"
                data-testid="header-phone"
                className={`hidden items-center gap-2 text-sm whitespace-nowrap transition-colors ${
                  showWhiteBg ? 'text-[#1A1A2E]/60 hover:text-[#F57C20]' : 'text-white/70 hover:text-white'
                }`}
              >
                <FiPhone className="w-4 h-4" />
                <span className="font-medium">+91 98200 88292</span>
              </a>

              {user ? (
                <div className="hidden md:flex items-center gap-3">
                  <Link
                    to={user.is_admin ? '/admin' : '/dashboard'}
                    data-testid="header-user-name"
                    className={`flex items-center gap-2 text-sm transition-colors ${
                      showWhiteBg ? 'text-[#1A1A2E]/70 hover:text-[#1A1A2E]' : 'text-white/70 hover:text-white'
                    }`}
                  >
                    <FiUser className="w-4 h-4 text-[#F57C20]" />
                    <span className="font-medium">{user.full_name?.split(' ')[0]}</span>
                  </Link>
                  <button
                    onClick={handleLogout}
                    data-testid="header-logout-btn"
                    className={`transition-colors ${showWhiteBg ? 'text-[#1A1A2E]/40 hover:text-[#1A1A2E]' : 'text-white/40 hover:text-white'}`}
                    title="Logout"
                  >
                    <FiLogOut className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <Link
                  to="/login"
                  data-testid="header-login-btn"
                  className={`hidden md:flex items-center gap-2 text-sm font-medium transition-colors ${
                    showWhiteBg ? 'text-[#1A1A2E]/70 hover:text-[#F57C20]' : 'text-white/80 hover:text-white'
                  }`}
                >
                  <FiUser className="w-4 h-4" />
                  Login
                </Link>
              )}

              <HeaderSplitCta quoteFirst={isHomePage} onQuote={() => setShowQuote(true)} />
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                data-testid="mobile-menu-toggle"
                className={`lg:hidden p-2 ${showWhiteBg ? 'text-[#1A1A2E]' : 'text-white'}`}
              >
                {isMobileMenuOpen ? <FiX className="w-6 h-6" /> : <FiMenu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div data-testid="mobile-menu" className="lg:hidden bg-white border-t border-gray-100 shadow-xl">
            <div className="px-6 py-8 space-y-5">
              {NAV_LINKS.map(link => (
                link.children ? (
                  <div key={link.path} className="space-y-3">
                    <p className="text-base font-medium text-[#1A1A2E]/70">{link.label}</p>
                    <div className="pl-4 space-y-2 border-l border-gray-100">
                      {link.children.map(child => (
                        <Link
                          key={child.path}
                          to={child.path}
                          data-testid={`mobile-nav-${child.label.toLowerCase().replace(/\s+/g, '-')}`}
                          className={`block text-sm ${location.pathname === child.path ? 'text-[#F57C20]' : 'text-[#1A1A2E]/60'}`}
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                ) : (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`block text-base font-medium ${
                      location.pathname === link.path ? 'text-[#F57C20]' : 'text-[#1A1A2E]/70'
                    }`}
                  >
                    {link.label}
                  </Link>
                )
              ))}
              <div className="border-t border-gray-100 pt-5 space-y-4">
                {user ? (
                  <>
                    <Link to={user.is_admin ? '/admin' : '/dashboard'} className="block text-[#1A1A2E]/70 text-sm font-medium">
                      <FiUser className="inline w-4 h-4 mr-2 text-[#F57C20]" />{user.full_name}
                    </Link>
                    <button onClick={handleLogout} className="text-[#1A1A2E]/40 text-sm font-medium">Logout</button>
                  </>
                ) : (
                  <Link to="/login" className="block text-[#F57C20] text-sm font-medium">
                    <FiUser className="inline w-4 h-4 mr-2" />Login / Register
                  </Link>
                )}
                <button
                  type="button"
                  data-testid="mobile-get-quote-btn"
                  onClick={() => { setIsMobileMenuOpen(false); setShowQuote(true); }}
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#1A1A2E] text-white px-6 py-3 text-sm font-semibold rounded"
                >
                  <FiFileText className="w-4 h-4" /> Get Off-Site Quote
                </button>
                <Link to="/villas" data-testid="mobile-book-now" className="block bg-[#F57C20] text-white text-center px-6 py-3 text-sm font-semibold rounded hover:bg-[#E06A10] transition-all">
                  Book Now
                </Link>
                <button
                  type="button"
                  data-testid="mobile-nav-join-hands"
                  onClick={() => { setIsMobileMenuOpen(false); setShowJoinHands(true); }}
                  className="w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#F57C20] to-[#D4621A] text-white px-6 py-3 text-sm font-semibold rounded"
                >
                  <FiBriefcase className="w-4 h-4" /> Join Hands With Us
                </button>
              </div>
            </div>
          </div>
        )}
      </header>
      <JoinHandsModal open={showJoinHands} onClose={() => setShowJoinHands(false)} />
      <QuoteModal open={showQuote} onClose={() => setShowQuote(false)} mode="guest" title="Get Your Off-Site Quote" />
    </>
  );
};

export default Header;
